import { useEffect, useRef, useState } from "react";
import { Send, Check, AlertCircle, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { show } from "@/lib/podcast";
import { siteConfig } from "@/config/siteConfig";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/xljjzjky";
const TURNSTILE_SITEKEY = "0x4AAAAAAFze6572Ia6BwNFJ";
const TOPICS = [
  "General Feedback",
  "Guest Suggestion",
  "Episode Idea",
  "Question for the Host",
  "Correction / Follow-Up",
];

const inputClass =
  "w-full rounded-sm border border-[#0b1f4d]/15 bg-[#f3ecdc] px-4 py-3 text-sm text-[#0b1f4d] placeholder:text-[#66718d] focus:border-[#f5b61c] focus-visible:ring-2 focus-visible:ring-[#f5b61c]/40 outline-none transition-colors";
const labelClass =
  "block font-mono text-xs uppercase tracking-[0.2em] text-[#4d5a78] mb-2";

export default function Contact() {
  const { contact } = siteConfig;
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [token, setToken] = useState("");
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);
  const loopRef = useRef(null);

  // Load the ListenerLoop embed. A <script> tag in JSX never executes, so it is
  // injected here inside the container (the embed locates itself via its tag).
  useEffect(() => {
    const host = loopRef.current;
    if (!host) return undefined;
    const s = document.createElement("script");
    s.src = "https://dash.listenerloop.app/embed.js";
    s.async = true;
    s.setAttribute("data-site-key", "pk_m3F0G0jBJU_a-Pf5zx3CwQ");
    host.appendChild(s);
    return () => {
      host.innerHTML = "";
    };
  }, []);

  // Load Cloudflare Turnstile and render the widget (dark theme).
  useEffect(() => {
    let cancelled = false;

    const render = () => {
      if (cancelled) return;
      const ts = window.turnstile;
      if (!ts || !widgetRef.current || widgetIdRef.current !== null) return;
      widgetIdRef.current = ts.render(widgetRef.current, {
        sitekey: TURNSTILE_SITEKEY,
        theme: "dark",
        callback: (t) => setToken(t),
        "expired-callback": () => setToken(""),
        "error-callback": () => setToken(""),
      });
    };

    if (window.turnstile) {
      render();
    } else if (!document.getElementById("cf-turnstile-script")) {
      const s = document.createElement("script");
      s.id = "cf-turnstile-script";
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      s.async = true;
      s.defer = true;
      s.onload = render;
      document.head.appendChild(s);
    } else {
      const iv = setInterval(() => {
        if (window.turnstile) {
          clearInterval(iv);
          render();
        }
      }, 200);
      return () => clearInterval(iv);
    }

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* noop */
        }
        widgetIdRef.current = null;
      }
    };
  }, []);

  const resetTurnstile = () => {
    setToken("");
    if (window.turnstile && widgetIdRef.current !== null) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {
        /* noop */
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    // Honeypot: if filled, silently pretend success.
    if (form._gotcha.value) {
      setStatus("success");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");

    const data = new FormData(form);
    data.set("cf-turnstile-response", token);

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        resetTurnstile();
      } else {
        const body = await res.json().catch(() => ({}));
        setErrorMsg(
          body?.errors?.map((x) => x.message).join(", ") ||
            "Something went wrong sending your message. Please try again.",
        );
        setStatus("error");
        resetTurnstile();
      }
    } catch {
      setErrorMsg(
        "We couldn't reach the server. Check your connection and try again.",
      );
      setStatus("error");
      resetTurnstile();
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact"
        description={`Get in touch with ${show.title}. ${contact.blurb}`}
        image={show.image}
        path="/contact"
      />

      <section className="hero-bg border-b border-[#0b1f4d]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#9a6700]">
            Get in touch
          </span>
          <h1 className="font-poster uppercase text-5xl sm:text-6xl text-[#0b1f4d] mt-2 tracking-tight">
            Contact WVEdChat
          </h1>
          <p className="mt-4 text-lg text-[#374566] leading-relaxed max-w-2xl">
            Have a question, comment, correction, guest suggestion, or episode idea?
            I'd love to hear from you.
          </p>
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div
          ref={loopRef}
          data-testid="contact-listenerloop"
          className="mb-8 flex w-full justify-center"
        />

        {status === "success" ? (
          <div
            data-testid="contact-success"
            className="rounded-lg border border-[#f5b61c]/40 bg-[#f5b61c]/10 p-8 text-center"
          >
            <div className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-[#f5b61c] text-[#0a1630]">
              <Check size={26} />
            </div>
            <h2 className="mt-5 font-display uppercase text-2xl font-semibold text-[#0b1f4d]">
              Message sent
            </h2>
            <p className="mt-2 text-[#374566]">
              Thanks for reaching out — your message is on its way to Randy.
            </p>
            <button
              data-testid="contact-send-another"
              onClick={() => setStatus("idle")}
              className="mt-6 inline-flex items-center gap-2 rounded-sm border border-[#0b1f4d]/20 hover:border-[#f5b61c] text-[#0b1f4d] font-semibold uppercase tracking-wide px-5 py-2.5 transition-colors"
            >
              Send another
            </button>
          </div>
        ) : (
          <form
            data-testid="contact-form"
            onSubmit={handleSubmit}
            className="rounded-lg border border-[#0b1f4d]/10 bg-white p-6 sm:p-8 space-y-6"
          >
            <div>
              <label htmlFor="name" className={labelClass}>
                Name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                placeholder="Your name"
                data-testid="contact-name"
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="email" className={labelClass}>
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="you@example.com"
                data-testid="contact-email"
                className={inputClass}
              />
            </div>

            <div>
              <label htmlFor="topic" className={labelClass}>
                Topic
              </label>
              <select
                id="topic"
                name="topic"
                data-testid="contact-topic"
                className={`${inputClass} appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%230b1f4d%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22M6%209l6%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:18px] bg-[right_1rem_center] bg-no-repeat pr-10`}
              >
                {TOPICS.map((t) => (
                  <option key={t} value={t} className="bg-white">
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="message" className={labelClass}>
                Message
              </label>
              <textarea
                id="message"
                name="message"
                rows={6}
                required
                placeholder="Type your message here..."
                data-testid="contact-message"
                className={`${inputClass} resize-y`}
              />
            </div>

            <input
              type="hidden"
              name="_subject"
              value="New WVEdChat Contact Form Submission"
            />
            {/* Honeypot */}
            <input
              type="text"
              name="_gotcha"
              tabIndex={-1}
              autoComplete="off"
              className="hidden"
              aria-hidden="true"
            />

            <div>
              <span className={labelClass}>Verification</span>
              <div ref={widgetRef} data-testid="contact-turnstile" />
            </div>

            {status === "error" && (
              <div
                data-testid="contact-error"
                className="flex items-start gap-2 rounded-sm border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
              >
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={status === "submitting"}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 rounded-sm bg-[#f5b61c] hover:bg-[#dda00d] text-[#0a1630] font-bold uppercase tracking-wide px-7 py-3.5 transition-colors disabled:opacity-60 disabled:pointer-events-none"
            >
              {status === "submitting" ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Send size={18} /> Send message
                </>
              )}
            </button>
          </form>
        )}

        <p className="mt-6 text-sm text-[#66718d]">
          Messages are sent directly to Randy. While every message is read, a
          personal response cannot always be guaranteed.
        </p>
      </section>
    </div>
  );
}
